import Button from '@carvana/ds-button';
import { CircleAlert } from 'lucide-react';
import Spinner from '@carvana/ds-spinner';
import { icon } from '@carvana/ds-styles';
import React from 'react';
import { EventedModal } from '|/app/modals';
import styles from './complete-purchase.module.css';
import { PayAtOrder } from './components/pay-at-order';
import { SimpleConfirm } from './components/simple-confirm';
import { AlertModalContent } from './components/slot-unavailable-modal/alert-modal-content';
import SlotUnavailableModal from './components/slot-unavailable-modal/slot-unavailable-modal';
import { PaymentProvider } from './contexts/payment';
import useCompletePurchaseVm from './use-complete-purchase-vm';
import type { CompletePurchaseProps } from './use-complete-purchase-vm';

const CompletePurchaseInner: React.FC<CompletePurchaseProps> = ({
  hasScheduleStep,
  editPaymentMethod,
  ...restProps
}) => {
  const {
    mode,
    slotPreference,
    paymentSummary,
    isLoading,
    isSubmitting,
    error,
    isSlotUnavailableModalOpen,
    isDocUploadErrorModalOpen,
    handleSubmit,
    handleSelectNewTime,
    handleDismissDocUploadError
  } = useCompletePurchaseVm({ hasScheduleStep, editPaymentMethod, ...restProps });

  const docUploadErrorModal = (
    <EventedModal
      isOpen={isDocUploadErrorModalOpen}
      analyticsId="pp_checkout_doc_upload_error"
      zIndex={10000000}
      closeIcon={false}
      divLine={false}
      desktopWidth="480px"
    >
      <AlertModalContent
        title="Something went wrong"
        subtext={
          hasScheduleStep
            ? 'Your appointment is scheduled, but we had an issue submitting your order. Please try again.'
            : 'We had an issue submitting your order. Please try again.'
        }
        icon={<CircleAlert size={24} color={icon.warning} />}
        action={
          <Button
            variant="primary"
            width="100%"
            onClick={handleDismissDocUploadError}
            data-analytics-id="pp_checkout_doc_upload_error_dismiss"
          >
            Continue
          </Button>
        }
      />
    </EventedModal>
  );

  if (isSlotUnavailableModalOpen && mode === null) {
    return (
      <>
        <div className={styles.loadingContainer}>
          <p className={styles.unavailableText}>Time slot unavailable</p>
        </div>
        <SlotUnavailableModal
          isOpen
          displayDate={slotPreference?.displayDate ?? ''}
          displayTime={slotPreference?.displayTime ?? ''}
          onSelectNewTime={handleSelectNewTime}
        />
      </>
    );
  }

  if (isLoading || mode === null) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorText}>{error}</p>
      </div>
    );
  }

  if (mode === 'pao') {
    return (
      <>
        <PayAtOrder onSubmit={handleSubmit} editPaymentMethod={editPaymentMethod} />
        <SlotUnavailableModal
          isOpen={isSlotUnavailableModalOpen}
          displayDate={slotPreference?.displayDate ?? ''}
          displayTime={slotPreference?.displayTime ?? ''}
          onSelectNewTime={handleSelectNewTime}
        />
        {docUploadErrorModal}
      </>
    );
  }

  return (
    <>
      <SimpleConfirm
        isSubmitting={isSubmitting}
        hasPaymentSummary={paymentSummary !== null}
        onSubmit={() => {
          void handleSubmit();
        }}
      />
      <SlotUnavailableModal
        isOpen={isSlotUnavailableModalOpen}
        displayDate={slotPreference?.displayDate ?? ''}
        displayTime={slotPreference?.displayTime ?? ''}
        onSelectNewTime={handleSelectNewTime}
      />
      {docUploadErrorModal}
    </>
  );
};

const CompletePurchase: React.FC<CompletePurchaseProps> = props => {
  return (
    <PaymentProvider>
      <div className={styles.container}>
        <CompletePurchaseInner {...props} />
      </div>
    </PaymentProvider>
  );
};

export default CompletePurchase;
